/**
 * 지도 썸네일 — 사진이 없는 장소 카드에 지도를 대신 깝니다.
 *
 * 홈이 이미 같은 일을 합니다("장소 사진이 없으면 지도가 배너를 채웁니다").
 * 다만 홈은 지도 컴포넌트를 띄우고, 카드 레일은 한 화면에 수십 장이 놓이므로
 * 여기서는 **타일 이미지 한 장**으로 그립니다.
 *
 * 왜 OSM 타일인가
 *   · 무료이고 API 키가 필요 없습니다. 구글 Static Maps 는 요청마다 과금됩니다.
 *   · 해외 지도(Leaflet)가 이미 같은 타일을 쓰므로 생김새가 일관됩니다.
 *   · <img> 한 장이라 개수가 늘어도 부담이 적고 브라우저 캐시가 듣습니다.
 *
 * 네이티브에 같은 파일이 있습니다(dokocrew_native `components/ds/mapThumb.ts`).
 * 줌·계산식이 어긋나면 같은 장소가 웹과 앱에서 다르게 보이므로 함께 고칩니다.
 */

/** 카드 썸네일용 기본 줌 — 동네가 구분될 만큼, 타일이 과하게 갈리지 않을 만큼 */
export const MAP_THUMB_ZOOM = 15;

/** 위경도 → 타일 좌표 (구면 메르카토르). OSM 표준 공식입니다. */
const toTile = (lat: number, lng: number, zoom: number): { x: number; y: number } => {
    const n = Math.pow(2, zoom);
    const x = Math.floor(((lng + 180) / 360) * n);
    const latRad = (lat * Math.PI) / 180;
    const y = Math.floor(
        ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
    );
    return {
        x: Math.min(Math.max(x, 0), n - 1),
        y: Math.min(Math.max(y, 0), n - 1),
    };
};

/** 한국 영역 대략 판정 — 스태틱맵 공급자를 고르는 용도입니다(정밀 국경 판정 아님) */
const isKoreaCoord = (lat: number, lng: number): boolean =>
    lat >= 33.0 && lat <= 38.7 && lng >= 124.5 && lng <= 130.0;

/**
 * 좌표 한 곳을 보여 주는 지도 이미지 URL.
 *
 * 국내는 카카오, 해외는 OSM 타일입니다.
 *   · 카카오 — 한글 지명이 나오고 좌표가 그림 한가운데 옵니다. 키가 필요 없는
 *     공개 엔드포인트이고, 카카오 장소 페이지가 쓰는 것과 같습니다.
 *   · OSM — 국내 밖에서는 카카오가 지도를 그리지 않아 타일로 대신합니다.
 *     타일 한 장이라 좌표가 가장자리에 걸릴 수 있지만 "그 동네" 를 보여 주는
 *     용도로는 충분합니다.
 * 둘 다 무료이고 요청당 과금이 없습니다(구글 Static Maps 는 과금됩니다).
 */
export const mapThumbUrl = (
    lat?: number | null,
    lng?: number | null,
    zoom: number = MAP_THUMB_ZOOM,
): string | null => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return null;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -85 || lat > 85) return null; // 메르카토르 한계
    /* (0,0) 은 기니만 앞바다입니다. 좌표가 없어서 0 으로 온 것이지 진짜 그 자리는
       아니므로, 카드마다 똑같은 바다 타일을 깔지 않고 지도를 포기합니다. */
    if (lat === 0 && lng === 0) return null;
    if (isKoreaCoord(lat, lng)) {
        /* 카카오는 경도,위도 순서입니다 — 뒤집으면 엉뚱한 곳이 나옵니다 */
        return 'https://staticmap.kakao.com/staticmap/og'
            + `?type=place&srs=wgs84&size=400x400&service=placeweb&m=${lng},${lat}`;
    }
    const { x, y } = toTile(lat, lng, zoom);
    return `https://tile.openstreetmap.org/${zoom}/${x}/${y}.png`;
};
